import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { API_BASE_URL, BASE_URL_MAPPING, REPAIR_SHOP_API_ENDPOINTS } from '../constants/claims.constants';
import { EnvironmentpickerService } from './environmentpicker.service';
import { HttpHeaderService } from './httpheader.service';
import { LoggerService } from './logger.service';

export interface RepairShopAddress {
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode: string;
  country?: string;
}

export interface VehicleMake {
  make: string;
  makeDescription: string;
}
export interface EntegralAddress {
  lineOne?: string;
  lineTwo?: string;
  lineThree?: string;
  administrativeAreaLevelOne?: string;
  administrativeAreaLevelTwo?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  coordinates?: { latitude: number; longitude: number };
  formattedAddress?: string;
}

export interface EntegralContact {
  emails?: string[];
  phones?: Array<{ extension?: string; type?: string; number?: string; primary?: boolean }>;
  websites?: string[];
  preference?: string;
}

export interface EntegralCreateClaimRequest {
  claimNumber?: string;
  userSource?: string;
  userType?: string;
  loss?: {
    date?: string;
    cause?: string;
    description?: string;
  };
  vehicle?: {
    vin?: string;
    registration?: string;
    registrationCountry?: string;
    registrationAdministrativeArea?: string;
    registrationDate?: string;
    year?: number;
    make?: string;
    model?: string;
    color?: string;
    drivable?: boolean;
    totalLoss?: boolean;
    airbagsDeployed?: boolean;
    memos?: string[];
    damageNotes?: string;
    primaryPointsOfImpact?: string[];
    secondaryPointsOfImpact?: string[];
    currentLocation?: EntegralAddress & {
      currentLocationType?: string;
    };
    type?: string;
    arrivalMethod?: string;
  };
}

export interface EntegralCreateClaimResponse {
  data: {
    claimId: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RepairShopService {
  private readonly entegralClaimIdSessionKey = 'oysExternalClaimId';
  private readonly entegralClaimVehicleSessionKey = 'oysExternalClaimVehicleSignature';
  private createEntegralClaimIdInFlight$?: Observable<string | null>;
  private createEntegralClaimIdInFlightVehicleSignature: string | null = null;

  constructor(
    private httpClient: HttpClient,
    private httpHeaderService: HttpHeaderService,
    private environmentPicker: EnvironmentpickerService,
    private logger: LoggerService
  ) {}

  private getEnv(): string {
    return environment.CLAIMS_DEV ? this.environmentPicker.getEnvironmentCookie() : 'prod';
  }

  private getBaseUrl(env: string): string {
    return BASE_URL_MAPPING[env] || API_BASE_URL.replace('${env}', env); // eslint-disable-line no-template-curly-in-string
  }

  private getStoredEntegralClaimId(): string | null {
    const storedClaimId = sessionStorage.getItem(this.entegralClaimIdSessionKey);
    return storedClaimId && storedClaimId.trim() ? storedClaimId.trim() : null;
  }

  private setStoredEntegralClaimId(claimId: string): void {
    sessionStorage.setItem(this.entegralClaimIdSessionKey, claimId);
  }

  private clearStoredEntegralClaimId(): void {
    sessionStorage.removeItem(this.entegralClaimIdSessionKey);
  }

  private getStoredEntegralVehicleSignature(): string | null {
    const storedVehicleSignature = sessionStorage.getItem(this.entegralClaimVehicleSessionKey);
    return storedVehicleSignature && storedVehicleSignature.trim() ? storedVehicleSignature.trim() : null;
  }

  private setStoredEntegralVehicleSignature(vehicleSignature: string): void {
    sessionStorage.setItem(this.entegralClaimVehicleSessionKey, vehicleSignature);
  }

  private clearStoredEntegralVehicleSignature(): void {
    sessionStorage.removeItem(this.entegralClaimVehicleSessionKey);
  }

  private clearStoredEntegralClaimContext(): void {
    this.clearStoredEntegralClaimId();
    this.clearStoredEntegralVehicleSignature();
  }

  private getVehicleSignature(vehicleData?: any): string | null {
    const normalizedYear = String(vehicleData?.year || '').trim();
    const normalizedMake = String(vehicleData?.make || '').trim().toUpperCase();
    const normalizedModel = String(vehicleData?.model || '').trim().toUpperCase();

    if (!normalizedYear && !normalizedMake && !normalizedModel) {
      return null;
    }

    return [normalizedYear, normalizedMake, normalizedModel].join('|');
  }

  private getVehicleDataFromRepairShopRequest(repairShopRequestObj: any): any {
    return {
      year: repairShopRequestObj?.year,
      make: repairShopRequestObj?.make,
      model: repairShopRequestObj?.model
    };
  }

  private isNullOrEmpty(value: any): boolean {
    return value === null || value === undefined || (typeof value === 'string' && !value.trim());
  }

  private getSessionId(): string | null {
    const sessionId = sessionStorage.getItem('sessionId');
    return sessionId && sessionId.trim() ? sessionId.trim() : null;
  }

  createUnauthEntegralClaimId(vehicleData?: any): Observable<string | null> {
    const env = this.getEnv();
    const uri = REPAIR_SHOP_API_ENDPOINTS.CREATE_ENTEGRAL_CLAIM_URL;
    const BASE_URL = this.getBaseUrl(env);
    const sessionId = this.getSessionId();

    const createEntergalClaimIdRequest: EntegralCreateClaimRequest = {
        claimNumber: sessionId,
        userSource: 'public_web',
        userType: 'customer',
        loss: {
            date: new Date().toISOString().split('T')[0],
            cause: 'UNKNOWN',
            description: 'from .com shop selection'
        },
        vehicle: {
            make: vehicleData?.make || 'UNKNOWN',
            model: vehicleData?.model || 'UNKNOWN',
            year: vehicleData?.year || new Date().getFullYear()
        }
    };

    this.logger.info('Create Entegral Claim Id Request', {
      endpoint: uri,
      fullRequest: createEntergalClaimIdRequest
    });

    return this.httpClient.post<EntegralCreateClaimResponse>(
      `${BASE_URL}${uri}`,
      createEntergalClaimIdRequest,
      { headers: this.httpHeaderService.getNWClaimServiceHeaders(environment.CLAIMS_DEV) }
    ).pipe(
      map((response: EntegralCreateClaimResponse) => response?.data?.claimId || null),
      tap((claimId: string | null) => {
        if (claimId) {
          this.setStoredEntegralClaimId(claimId);
          const vehicleSignature = this.getVehicleSignature(vehicleData);
          if (vehicleSignature) {
            this.setStoredEntegralVehicleSignature(vehicleSignature);
          } else {
            this.clearStoredEntegralVehicleSignature();
          }
          this.logger.info('Create Entegral Claim Id Response', { claimId });
        }
      }),
      catchError((error: any) => {
        this.logger.error('Create Entegral Claim Id API failed', error);
        return of(null);
      })
    );
  }

  private resolveExternalClaimId(repairShopRequestObj: any): Observable<string | null> {
    const externalClaimId = repairShopRequestObj?.externalClaimId;
    if (!this.isNullOrEmpty(externalClaimId)) {
      return of((externalClaimId as string).trim());
    }

    const requestVehicleData = this.getVehicleDataFromRepairShopRequest(repairShopRequestObj);
    const requestVehicleSignature = this.getVehicleSignature(requestVehicleData);

    const storedClaimId = this.getStoredEntegralClaimId();
    const storedVehicleSignature = this.getStoredEntegralVehicleSignature();
    if (storedClaimId) {
      if (!requestVehicleSignature || !storedVehicleSignature || requestVehicleSignature === storedVehicleSignature) {
        return of(storedClaimId);
      }

      this.logger.info('Vehicle changed - creating a new Entegral claim id', {
        previousVehicleSignature: storedVehicleSignature,
        currentVehicleSignature: requestVehicleSignature
      });
      this.clearStoredEntegralClaimContext();
    }

    if (this.isNullOrEmpty(repairShopRequestObj?.claimId)) {
      if (this.createEntegralClaimIdInFlight$ && this.createEntegralClaimIdInFlightVehicleSignature === requestVehicleSignature) {
        return this.createEntegralClaimIdInFlight$;
      }

      this.createEntegralClaimIdInFlightVehicleSignature = requestVehicleSignature;
      this.createEntegralClaimIdInFlight$ = this.createUnauthEntegralClaimId(requestVehicleData).pipe(
        finalize(() => {
          this.createEntegralClaimIdInFlight$ = undefined;
          this.createEntegralClaimIdInFlightVehicleSignature = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      return this.createEntegralClaimIdInFlight$;
    }

    return of(null);
  }

  buildGetOYSRepairShop(repairShopRequestObj: any): Observable<any> {
    const env = this.getEnv();

    return this.resolveExternalClaimId(repairShopRequestObj).pipe(
      switchMap((resolvedExternalClaimId: string | null) => {
        if (this.isNullOrEmpty(resolvedExternalClaimId)) {
          const errorMsg = 'OYS Repair Shop Request failed - externalClaimId is unavailable';
          this.logger.error(errorMsg, {
            claimId: repairShopRequestObj?.claimId || null,
            externalClaimId: resolvedExternalClaimId
          });
          return throwError(() => new Error(errorMsg));
        }

        const getOYSRepairShopRequest = {
          claimId: resolvedExternalClaimId,
          clientNetworkKey: REPAIR_SHOP_API_ENDPOINTS.ENTEGRAL_DEFAULT_CLIENT_NETWORK_KEY,
          vehicleIncidentId: repairShopRequestObj?.vehicleIncidentId || null,
          radius: repairShopRequestObj?.radius || 25,
          radiusUnits: 'MILES',
          address: {
            lineOne: repairShopRequestObj?.addressLine1,
            administrativeAreaLevelOne: repairShopRequestObj?.state,
            city: repairShopRequestObj?.city,
            country: repairShopRequestObj?.country || 'US',
            postalCode: repairShopRequestObj?.zipCode
          },
          userType: repairShopRequestObj?.userType,
          userSource: repairShopRequestObj?.userSource,
          enableSmartFilters: true,
          includeOutOfNetwork: false
        };

        this.logger.info('OYS Repair Shop Request', {
          endpoint: REPAIR_SHOP_API_ENDPOINTS.SEARCH_URL,
          claimId: getOYSRepairShopRequest.claimId,
          externalClaimId: getOYSRepairShopRequest.clientNetworkKey,
          address: getOYSRepairShopRequest.address,
          fullRequest: getOYSRepairShopRequest
        });

        const uri = REPAIR_SHOP_API_ENDPOINTS.SEARCH_URL;
        const BASE_URL = this.getBaseUrl(env);
        return this.httpClient.post(
          `${BASE_URL}${uri}`,
          getOYSRepairShopRequest,
          { headers: this.httpHeaderService.getNWClaimServiceHeaders(environment.CLAIMS_DEV) }
        ).pipe(
          // search-v2 wraps the useful payload in `data`; expose just that shape to callers.
          map((response: any) => response?.data ?? response)
        );
      })
    );
  }

  getTypeAheadSuggestions(address: string): Observable<any> {
    const uri = `${REPAIR_SHOP_API_ENDPOINTS.TYPE_AHEAD_URL}?text=${address}`;
    const BASE_URL = this.getBaseUrl(this.getEnv());
    return this.httpClient.get(
      `${BASE_URL}${uri}`,
      { headers: this.httpHeaderService.getAPILocationServiceHeaders() }
    );
  }

  getVehicleMakes(year: string): Observable<any> {
    const query = `coveredObject=AU&echoBack=false&modelYear=${year}&client_id=${environment.appProps.appClientId}`;
    const uri = `${REPAIR_SHOP_API_ENDPOINTS.VEHICLE_MAKES_URL}?${query}`;
    const BASE_URL = this.getBaseUrl(this.getEnv());
    return this.httpClient.get(`${BASE_URL}${uri}`);
  }

  getVehicleModels(year: string, make: string): Observable<any> {
    const query =
      `coveredObject=AU&echoBack=false&modelYear=${year}&make=${make}&client_id=${environment.appProps.appClientId}`;
    const uri = `${REPAIR_SHOP_API_ENDPOINTS.VEHICLE_MODELS_URL}?${query}`;
    const BASE_URL = this.getBaseUrl(this.getEnv());
    return this.httpClient.get(`${BASE_URL}${uri}`);
  }

  parseAddress(addressline: string): RepairShopAddress {
    const addressArray = addressline.split(',');
    return {
      addressLine1: addressArray[0]?.trim(),
      city: addressArray[1]?.trim(),
      state: addressArray[2]?.trim(),
      zipCode: addressArray[3]?.trim(),
      country: addressArray[4]?.trim() === 'USA' ? 'US' : addressArray[4]?.trim()
    };
  }

  getSelectedAddress(selectedAddress: RepairShopAddress): string {
    if (selectedAddress.addressLine1 !== undefined || selectedAddress.city !== undefined || selectedAddress.state !== undefined) {
      const { addressLine1, city, state, zipCode } = selectedAddress;
      return [addressLine1, city, state, zipCode].filter(Boolean).join(', ');
    }
    return selectedAddress.zipCode;
  }
}
